/**
 * B2BGate.jsx — Porte d'entrée vers la plaquette B2B.
 *
 * Flux :
 *   1. ?token=XXX dans l'URL → vérifie le token → redirige vers le vault
 *   2. Admin connecté → bouton "Ouvrir la plaquette" (vérifie + redirige)
 *   3. Sinon → écran "Accès restreint" avec champ pour entrer un code partenaire
 *      + lien discret vers /login pour l'admin.
 *
 * ⚠ Cette page ne doit JAMAIS afficher le contenu de la plaquette, uniquement
 *    rediriger ou refuser. La plaquette elle-même vit dans un dossier statique
 *    au slug secret (voir backend B2B_VAULT_SLUG).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Shield, Lock, ArrowRight, KeyRound, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useApp } from '../contexts/AppContext';
import api from '../api';
import { toast } from 'sonner';

export default function B2BGate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useApp() || {};
  const urlToken = params.get('token');

  const [status, setStatus] = useState('idle'); // idle | checking | denied | manual
  const [codeInput, setCodeInput] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const openVault = useCallback((vaultUrl) => {
    // Full page replace so service worker scope of the PWA takes over.
    window.location.replace(vaultUrl);
  }, []);

  const tryAccess = useCallback(async (tokenValue = null) => {
    setStatus('checking');
    setErrorMsg('');
    try {
      const res = await api.b2bPlaquetteAccess(tokenValue);
      if (res?.ok && res.vault_url) {
        openVault(res.vault_url);
        return;
      }
      setStatus('denied');
      setErrorMsg('Accès refusé.');
    } catch (e) {
      setStatus('manual');
      if (tokenValue) {
        setErrorMsg(e?.message || 'Code invalide ou expiré.');
      } else {
        setErrorMsg('');
      }
    }
  }, [openVault]);

  // Auto-attempt on load: token in URL first, then admin JWT if present
  useEffect(() => {
    if (urlToken && urlToken.length >= 10) {
      tryAccess(urlToken);
      return;
    }
    if (user && user.role === 'admin') {
      tryAccess(null);
      return;
    }
    setStatus('manual');
  }, [urlToken, user, tryAccess]);

  const onSubmit = (e) => {
    e.preventDefault();
    const v = codeInput.trim();
    if (v.length < 10) {
      setErrorMsg('Code trop court.');
      return;
    }
    tryAccess(v);
  };

  // ───────── Loading state ─────────
  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center p-6" data-testid="b2b-gate-loading">
        <div className="text-white/70 flex items-center gap-3">
          <Loader2 size={20} className="animate-spin" />
          <span className="text-sm">Vérification de l&apos;accès…</span>
        </div>
      </div>
    );
  }

  // ───────── Manual gate ─────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0b] via-[#111113] to-[#1a1a1d] flex items-center justify-center p-4 sm:p-6" data-testid="b2b-gate">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-red-600 text-white flex items-center justify-center shadow-lg shadow-red-900/40">
            <Shield size={26} />
          </div>
        </div>

        <Card className="border-white/10 bg-[#0d0d0e] text-white">
          <CardContent className="p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">code2roues.fr · B2B</div>
              <h1 className="text-2xl font-black leading-tight mb-2">Espace partenaires</h1>
              <p className="text-sm text-white/60 leading-relaxed">
                Cette plaquette commerciale est réservée aux moto-écoles et prospects invités.
              </p>
            </div>

            {user && user.role === 'admin' ? (
              <div className="space-y-3">
                <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/15 text-green-400 flex items-center justify-center shrink-0">
                    <Shield size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">Connecté en admin</div>
                    <div className="text-xs text-white/50 truncate">{user.email}</div>
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={() => tryAccess(null)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-12 rounded-xl"
                  data-testid="b2b-gate-admin-open"
                >
                  Ouvrir la plaquette
                  <ArrowRight size={16} className="ml-1" />
                </Button>
                <Link
                  to="/admin/b2b-plaquette"
                  className="block text-center text-xs text-white/50 hover:text-white/80 transition pt-2"
                  data-testid="b2b-gate-admin-manage"
                >
                  → Gérer les codes d&apos;accès partenaires
                </Link>
              </div>
            ) : (
              <>
                <form onSubmit={onSubmit} className="space-y-3" data-testid="b2b-gate-form">
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-bold flex items-center gap-1.5 mb-2">
                      <KeyRound size={12} /> Code d&apos;accès
                    </span>
                    <Input
                      type="text"
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.target.value)}
                      placeholder="Collez votre code partenaire ici…"
                      autoComplete="off"
                      spellCheck={false}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-xl font-mono text-sm"
                      data-testid="b2b-gate-code-input"
                    />
                  </label>

                  {errorMsg && (
                    <div className="flex items-start gap-2 rounded-lg bg-red-500/10 border border-red-500/20 p-3" data-testid="b2b-gate-error">
                      <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
                      <div className="text-xs text-red-300 leading-relaxed">{errorMsg}</div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-12 rounded-xl"
                    disabled={codeInput.trim().length < 10}
                    data-testid="b2b-gate-submit"
                  >
                    <Lock size={14} className="mr-2" />
                    Accéder à la plaquette
                  </Button>
                </form>

                <div className="mt-6 pt-5 border-t border-white/10 text-center space-y-2">
                  <div className="text-[11px] text-white/40 leading-relaxed">
                    Vous êtes une moto-école et souhaitez recevoir un code ?<br />
                    Contactez-nous pour une démonstration.
                  </div>
                  <Link
                    to="/login?redirect=/admin/b2b-plaquette"
                    className="inline-block text-[11px] text-white/40 hover:text-white/70 transition underline underline-offset-4"
                    data-testid="b2b-gate-admin-login"
                  >
                    Admin · Se connecter
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link to="/" className="text-xs text-white/30 hover:text-white/60 transition" data-testid="b2b-gate-home">
            ← Retour au site principal
          </Link>
        </div>
      </div>
    </div>
  );
}
